import { FileCategory, useFileValidation } from "@/modules/shared/hooks/use-file-validation"
import { Label } from "@/modules/shared/ui/components/label"
import { Upload } from "lucide-react"
import { forwardRef, useImperativeHandle, useState } from "react"

interface DraggableFileUploadProps {
    fileCategory: FileCategory
    onFileSelected: (file: File) => void
}

export interface DraggableFileUploadRef {
    clearError: () => void
}

export const DraggableFileUpload = forwardRef<DraggableFileUploadRef, DraggableFileUploadProps>(
    ({ fileCategory, onFileSelected }, ref) => {
        const { fileError, clearError, validateAndSetFile } = useFileValidation({
            category: fileCategory,
        })

        useImperativeHandle(ref, () => ({ clearError }))

        const [isDragging, setIsDragging] = useState(false)

        const handleDragOver = (e: React.DragEvent) => {
            e.preventDefault()
            setIsDragging(true)
        }

        const handleDragLeave = (e: React.DragEvent) => {
            e.preventDefault()
            setIsDragging(false)
        }

        const handleDrop = (e: React.DragEvent) => {
            e.preventDefault()
            setIsDragging(false)
            Array.from(e.dataTransfer.files).forEach((file) => {
                validateAndSetFile(file, onFileSelected)
            })
        }

        return (
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging
                        ? "border-blue-500 bg-blue-50"
                        : fileError
                            ? "border-red-400"
                            : "border-gray-300 hover:border-gray-400"
                    }`}
            >
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-sm text-gray-600 mb-1">
                    Drag and drop your file here, or
                </p>
                <Label
                    htmlFor="file-upload"
                    className="justify-center cursor-pointer text-blue-600 hover:text-blue-700 font-medium mb-2"
                >
                    browse files
                </Label>
                <input
                    id="file-upload"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                        if (!e.target.files) return
                        clearError()
                        Array.from(e.target.files).forEach((file) => {
                            validateAndSetFile(file, onFileSelected)
                        })
                        e.target.value = ""
                    }}
                />
                <p className="text-xs text-gray-500 mt-2">PDF, DOC, or DOCX</p>
                {fileError && (
                    <p className="mt-2 text-xs text-red-600">{fileError}</p>
                )}
            </div>
        )
    })
